<script setup lang="ts">
import IconDot from '../Icons/IconDot.vue'

defineProps({
    isContainedInPopup: {
        type: Boolean,
        default: false
    }
})

const emits = defineEmits(['closePopUp'])

const config = useRuntimeConfig()
const { locale } = useI18n()

const activeIndex = ref(1)
const maxCarouselIndex = ref(2)
const tick: Ref<NodeJS.Timeout | string | number | undefined> = ref(undefined)

const getCarouselButtonClass = (index: number) =>
    activeIndex.value === index
        ? 'text-button-primary-branding-hover'
        : 'text-gray-l-500'

const onClickOn = (link: string) => {
    if (window.location !== window?.parent?.location) {
        ;(window.top as Window).postMessage(
            { action: 'openUrl', url: link },
            document.referrer
        )
    } else {
        const newWnd = window.open(
            getChargemapUrl(`/${link}`, locale.value),
            '_top'
        )

        if (newWnd) {
            newWnd.opener = null
        }
    }
}

const resetInterval = () => {
    clearInterval(tick.value)
    tick.value = setInterval(() => {
        activeIndex.value = activeIndex.value === 1 ? 2 : 1
    }, 5000)
}

const onClickCarouselButton = (index: number) => {
    activeIndex.value = index
    resetInterval()
}

onMounted(() => resetInterval())
onUnmounted(() => {
    if (tick.value) {
        clearInterval(tick.value)
    }
})
</script>

<template>
    <div
        class="my-auto flex flex-col gap-3 425:gap-5 p-4 max-w-md"
        :class="{
            'h-screen': isContainedInPopup
        }"
    >
        <div
            class="flex flex-col justify-center gap-2 text-center text-gray-l-700 font-bold my-auto"
            :class="{
                'mx-auto': !isContainedInPopup,
                'mx-11 425:mx-12': isContainedInPopup
            }"
        >
            <h2
                class="font-bold text-2xl 425:text-32 tracking-light cursor-pointer gtm-InscriptionConnexionMap"
                data-testid="join-chargemap-title"
                @click="onClickOn('register')"
            >
                <span class="text-button-primary-branding-hover">
                    {{ $t('join_chargemap.title1') }}
                </span>
                {{ $t('join_chargemap.title2') }}
            </h2>
            <IconCross
                v-if="isContainedInPopup"
                class="text-gray-l-700 cursor-pointer absolute top-5 right-5 h-4"
                @click="emits('closePopUp')"
            />
            <h3
                class="text-fifteen-exploitation 425:text-seventeen-exploitation leading-4.5 425:leading-5 cursor-pointer gtm-InscriptionConnexionMap"
                data-testid="join-chargemap-subtitle"
                @click="onClickOn('register')"
            >
                {{ $t(`join_chargemap.subtitle${activeIndex}`) }}
            </h3>
        </div>
        <div
            id="user-not-connected-carousel"
            class="flex flex-col justify-center items-center gap-4 mx-4 425:mx-12 text-center my-auto"
        >
            <img
                class="cursor-pointer px-6 mb-1 w-48 gtm-InscriptionConnexionMap"
                loading="lazy"
                :src="`${config.app.cdnURL}/images/UserNotConnectedCarousel${activeIndex}.svg`"
                :class="{
                    '425:w-full': isContainedInPopup,
                    '425:w-9/12': !isContainedInPopup
                }"
                :alt="$t(`join_chargemap.desc${activeIndex}`)"
                @click="onClickOn('register')"
            />
            <div class="flex gap-2 justify-center text-xxs">
                <div
                    v-for="index in maxCarouselIndex"
                    :key="index"
                    :class="getCarouselButtonClass(index)"
                >
                    <IconDot
                        class="h-2 cursor-pointer"
                        @click="onClickCarouselButton(index)"
                    />
                </div>
            </div>
            <span
                class="font-normal text-fifteen-exploitation 425:text-seventeen-exploitation leading-4.5 425:leading-5 tracking-light text-gray-l-700"
            >
                {{ $t(`join_chargemap.desc${activeIndex}`) }}
            </span>
        </div>
        <div
            class="flex flex-col justify-center gap-2 text-center my-auto"
            :class="{ 'pb-4': isContainedInPopup }"
        >
            <ChargemapB2CButton
                type="primary"
                size="large"
                class="primary gtm-InscriptionConnexionMap"
                @click="onClickOn('register')"
            >
                {{ $t('join_chargemap.signup') }}
            </ChargemapB2CButton>
            <div
                class="flex items-center justify-center gap-1 425:gap-2 pt-3.5"
            >
                <span
                    class="text-thirteen-exploitation 425:text-seventeen-exploitation leading-4 425:leading-5 tracking-light gtm-InscriptionConnexionMap"
                >
                    {{ $t('join_chargemap.signin1') }}
                </span>
                <ChargemapB2CButton
                    type="tertiary"
                    class="p-0 text-thirteen-exploitation 425:text-seventeen-exploitation"
                    @click="onClickOn('signin')"
                >
                    {{ $t('join_chargemap.signin2') }}
                </ChargemapB2CButton>
            </div>
        </div>
    </div>
</template>
